import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { TrackEvent } from '@bnnvara/analytics';
import createEmbedCode from '../../util/embed';
import decodeHtml from '../../util/decodeHtml';
import Icon from '../Icon';
import * as S from './styled';

/**
 *
 * @param {string} string
 */
function copyStringToClipboard(string) {
  function handler(event) {
    event.clipboardData.setData('text/plain', string);
    event.preventDefault();
    document.removeEventListener('copy', handler, true);
  }

  document.addEventListener('copy', handler, true);
  document.execCommand('copy');
}

/**
 *
 * @param {object} el
 * @param {function} setStateFn
 */
const detectClickOutSideElement = (el, setStateFn) => {
  window.addEventListener('click', e => {
    if (el !== e.target) {
      setStateFn(false);
      window.removeEventListener('click', () => {});
    } else {
      setStateFn(true);
    }
  });
};

/**
 *
 * @param {function} callback
 */
const closeShareModalAfterDelay = (callback, isLink) => {
  let timeOut;
  // eslint-disable-next-line prefer-const
  timeOut = setTimeout(() => {
    callback({ isCopiedToClipboard: true, isLink });
    clearTimeout(timeOut);
  }, 1000);
};

const Share = ({ mediaId, detailPageUrl, toggleShareFn }) => {
  const [linkIsCopied, setLinkIsCoppied] = useState(false);
  const [embedIsCopied, setEmbedIsCoppied] = useState(false);

  const copyToClipboard = e => {
    const element = e.target;
    e.preventDefault();
    navigator.clipboard
      .writeText(detailPageUrl)
      .then(() => {
        setLinkIsCoppied(true);
        closeShareModalAfterDelay(toggleShareFn, true);
      })
      .catch(err => {
        setLinkIsCoppied(true);
        copyStringToClipboard(detailPageUrl);
        // eslint-disable-next-line no-console
        console.warn(
          'Could not copy text with `navigator.clipboard` so we used `clipboardData.setData` ass fallback: ',
          err
        );
        closeShareModalAfterDelay(toggleShareFn, true);
      });
    detectClickOutSideElement(element, setLinkIsCoppied);

    // Ga track event
    TrackEvent(
      'video-player-share',
      'clickCopyLinkButton',
      'link-copied-to-clipboard'
    );
  };

  const copyEmbedToClipBoard = e => {
    const element = e.target;
    e.preventDefault();

    const embedLink = createEmbedCode({
      params: {
        id: mediaId,
      },
    });

    navigator.clipboard
      .writeText(decodeHtml(embedLink))
      .then(() => {
        setEmbedIsCoppied(true);
        closeShareModalAfterDelay(toggleShareFn, false);
      })
      .catch(err => {
        // Fallback for Chrome
        copyStringToClipboard(decodeHtml(embedLink));
        setEmbedIsCoppied(true);
        // eslint-disable-next-line no-console
        console.warn(
          'Could not copy text with `navigator.clipboard` so we used `clipboardData.setData` ass fallback: ',
          err
        );
        closeShareModalAfterDelay(toggleShareFn, true);
      });

    detectClickOutSideElement(element, setEmbedIsCoppied);

    // Ga track event
    TrackEvent(
      'video-player-share',
      'clickCopyEmbedButton',
      'embed-copied-to-clipboard'
    );
  };

  const gaTrackEvent = type => {
    TrackEvent(
      'video-player-share',
      `clickShareButton${type.toUpperCase()}`,
      `shares-video-on-${type}`
    );
  };

  return (
    <S.Modal>
      <S.ModalInner>
        <S.TopWrapper>
          Video delen
          <S.Button type="button" onClick={toggleShareFn}>
            <Icon icon="cross" />
          </S.Button>
        </S.TopWrapper>
        <S.BodyWrapper>
          <S.IconWrapper
            href={`https://api.whatsapp.com/send?text=${detailPageUrl}`}
            data-action="share/whatsapp/share"
            target="_blank"
            rel="noopener"
            className="icon-whatsapp"
            onClick={() => {
              gaTrackEvent('whatsapp');
            }}
          >
            <Icon icon="whatsapp" size="25" color="#fff" />
            <S.Label>Whatsapp</S.Label>
          </S.IconWrapper>
          <S.IconWrapper
            href={`https://www.facebook.com/sharer.php?u=${detailPageUrl}`}
            target="_blank"
            rel="noopener"
            onClick={() => {
              gaTrackEvent('facebook');
            }}
          >
            <Icon icon="facebook" size="25" color="#fff" />
            <S.Label>Facebook</S.Label>
          </S.IconWrapper>
          <S.IconWrapper
            target="_blank"
            rel="noopener"
            href={`http://www.linkedin.com/shareArticle?url=${detailPageUrl}`}
            onClick={() => {
              gaTrackEvent('linkedin');
            }}
          >
            <Icon icon="linkedin" size="25" color="#fff" />
            <S.Label>Linkedin</S.Label>
          </S.IconWrapper>
          <S.IconWrapper
            target="_blank"
            rel="noopener"
            href={`https://twitter.com/intent/tweet?url=${detailPageUrl}`}
            onClick={() => {
              gaTrackEvent('twitter');
            }}
          >
            <Icon icon="twitter" size="25" color="#fff" />
            <S.Label>Twitter</S.Label>
          </S.IconWrapper>
          <S.IconWrapper
            linkIsCopied={linkIsCopied}
            href={detailPageUrl}
            onClick={copyToClipboard}
          >
            {linkIsCopied ? (
              <Icon icon="check" size="25" color="#000" />
            ) : (
              <Icon icon="copy-link" size="25" color="#fff" />
            )}

            <S.Label>Link kopiëren</S.Label>
          </S.IconWrapper>
          <S.IconWrapper
            embedIsCopied={embedIsCopied}
            onClick={copyEmbedToClipBoard}
            className="icon-embed"
          >
            {embedIsCopied ? (
              <Icon icon="check" size="25" color="#000" />
            ) : (
              <Icon icon="embed" size="25" color="#fff" />
            )}
            <S.Label>Embed kopiëren</S.Label>
          </S.IconWrapper>
        </S.BodyWrapper>
      </S.ModalInner>
    </S.Modal>
  );
};

Share.propTypes = {
  mediaId: PropTypes.string.isRequired,
  detailPageUrl: PropTypes.string.isRequired,
  toggleShareFn: PropTypes.func.isRequired,
};

export default Share;
